import { notFound } from "@tanstack/react-router";
import { gamePresentationOf } from "@softmax/web-features/coworlds";
import { matchRoute } from "@softmax/web-addresses";
import { requireGameResource } from "./data/context";
import { readPitchPage } from "@/features/desk/records";
import { deskFor } from "@/features/desk/records";
import { readCreatorUpload } from "@/features/coworlds/data/index";
import { readFrontDoor } from "@/features/preview/data/index";

import { gameTheaterQueryKeys, type GamePageContext } from "./game-types";
import { readGameTheater } from "./game-theater";

function gameIndexSearch(
  parameters: URLSearchParams,
): Record<string, string | readonly string[] | undefined> {
  const search: Record<string, string | readonly string[] | undefined> = {};

  for (const key of new Set(parameters.keys())) {
    const values = parameters.getAll(key);
    search[key] = values.length === 1 ? values[0] : values;
  }

  return search;
}

function gameIndexPresentation({
  route,
  game,
  search,
}: {
  readonly route: ReturnType<typeof matchRoute>;
  readonly game: GamePageContext;
  readonly search: URLSearchParams;
}): "desk" | "upload" | "sugarscape" | "home" | "theater" {
  if (
    route.name === "game.desk" ||
    (game.mode === "league" && game.presentation === "desk")
  )
    return "desk";

  if (game.mode === "upload") return "upload";

  if (
    game.league?.requestedSlug === null &&
    !gameTheaterQueryKeys.some((key) => search.has(key)) &&
    gamePresentationOf(game.corpusSlug).home === "sugarscape"
  )
    return "sugarscape";

  return game.presentation;
}

export async function readGameIndex({
  signal,
  href,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly href: string;
}) {
  const url = new URL(href, window.location.href);
  const search = gameIndexSearch(url.searchParams);

  const route = matchRoute(url.pathname);

  if (!["game.home", "game.league", "game.desk"].includes(route.name))
    throw notFound();

  const game = await requireGameResource({ signal, href });
  const presentation = gameIndexPresentation({
    route,
    game,
    search: url.searchParams,
  });

  if (presentation === "desk") {
    const spec = deskFor(game.coworldName);

    if (!spec || !game.league || !game.coworldName) throw notFound();

    const pitches = await readPitchPage({
      signal,
      leagueId: game.league.id,
      cursor: null,
    });
    return { kind: "desk" as const, game, pitches, spec };
  }

  if (game.mode === "upload") {
    const data = await readCreatorUpload({
      signal,
      name: game.coworldName,
      id: game.coworldId,
      unlisted: game.address.catalog_status === "unlisted",
    });
    return { kind: "upload" as const, game, data };
  }

  if (presentation === "sugarscape")
    return { kind: "sugarscape" as const, game };

  if (presentation === "home") {
    const data = await readFrontDoor({ signal, context: game, search });

    if (!data) throw notFound();

    return { kind: "home" as const, game, data };
  }

  const data = await readGameTheater({
    signal,
    href,
    game,
  });
  return { kind: "theater" as const, game, data };
}

export type GameIndexPage = Awaited<ReturnType<typeof readGameIndex>>;
